/**
 * Утилиты раскладки колонок для страницы view.
 * Pure-функции без React-зависимостей, легко тестируются и переиспользуются.
 */

import type { ViewConfig } from "@/lib/views";
import { ACTION_DIARY_DAY_COLUMN_MIN_WIDTH, isActionDiaryDayColumnKey } from "@/lib/action-diary-columns";
import { ORDERS_WEEK_COLUMN_MIN_WIDTH } from "@/lib/orders-weeks-columns";
import { isWeekCalendarColumnKey } from "@/lib/week-calendars";

export type ColInfo = {
  key: string;
  label: string;
  width: string | number;
  minWidth: number;
  truncate: boolean;
  /** Индекс колонки в полном списке visibleCols. Нужен, когда таблица
   *  рисует только горизонтально видимый кусок колонок. */
  sourceIndex?: number;
};

export type HeaderSeg =
  | { type: "solo"; col: ColInfo; ci: number }
  | { type: "group"; label: string; cols: ColInfo[]; startCi: number };

/** Ширина служебной ячейки «+ колонка» в самом конце шапки. Учитывается в общей
 *  ширине таблицы, иначе кнопка вылезает за рамку и появляется лишний скролл. */
export const ADD_COLUMN_CELL_WIDTH = 34;

const DEFAULT_AUTO_COLUMN_WIDTH = 120;
const DEFAULT_RESIZE_MIN_WIDTH = 80;
const PRODUCT_NAME_MIN_WIDTH = 48;
const OFFER_ID_MIN_WIDTH = 196;
const ORDERS_DAILY_MIN_WIDTH = 192;

export function columnWidthToNumber(width: string | number | undefined, fallback = DEFAULT_AUTO_COLUMN_WIDTH) {
  if (typeof width === "number") return width;
  if (width === "auto" || width == null) return fallback;
  const parsed = parseInt(String(width), 10);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export function getColumnResizeMinWidth(key: string, _baseWidth: string | number | undefined) {
  void _baseWidth;
  if (key === "name") return PRODUCT_NAME_MIN_WIDTH;
  if (key === "offer_id") return OFFER_ID_MIN_WIDTH;
  if (key === "orders_daily" || key === "ctr_daily_30d") return ORDERS_DAILY_MIN_WIDTH;
  if (isActionDiaryDayColumnKey(key)) return ACTION_DIARY_DAY_COLUMN_MIN_WIDTH;
  if (isWeekCalendarColumnKey(key)) return ORDERS_WEEK_COLUMN_MIN_WIDTH;
  return DEFAULT_RESIZE_MIN_WIDTH;
}

export function getColumnPixelWidth(col: ColInfo, activeView?: Pick<ViewConfig, "columnWidths"> | null) {
  const raw = activeView?.columnWidths?.[col.key] ?? col.width;
  return Math.max(col.minWidth, columnWidthToNumber(raw));
}

/**
 * Режет визуальные колонки на сегменты хедера: соседние колонки из одной
 * группы (`activeView.colGroups`) объединяются в один сегмент с общим
 * заголовком группы, остальные — как одиночные.
 */
export function computeHeaderSegments(
  visibleCols: ColInfo[],
  colGroups: ViewConfig["colGroups"],
): HeaderSeg[] {
  const colGroupOf: Record<string, string> = {};
  for (const g of colGroups ?? []) {
    for (const k of g.cols) colGroupOf[k] = g.label;
  }
  const segs: HeaderSeg[] = [];
  let i = 0;
  while (i < visibleCols.length) {
    const c = visibleCols[i];
    const gl = colGroupOf[c.key];
    if (gl) {
      const gcs = [c];
      let j = i + 1;
      while (j < visibleCols.length && colGroupOf[visibleCols[j].key] === gl) {
        gcs.push(visibleCols[j]);
        j++;
      }
      segs.push({ type: "group", label: gl, cols: gcs, startCi: i });
      i = j;
    } else {
      segs.push({ type: "solo", col: c, ci: i });
      i++;
    }
  }
  return segs;
}
